export type AvatarGender = "woman" | "man";
export type AvatarSlot = "face" | "hair" | "outfit" | "accessory" | "idle";

export interface CatalogItem {
  readonly key: string;
  readonly label: string;
  readonly accent: string;
}

const woman = {
  face: [
    { key: "face-w-soft", label: "柔和鹅蛋", accent: "#efc8b7" },
    { key: "face-w-clear", label: "清秀圆润", accent: "#e8bea9" },
    { key: "face-w-defined", label: "明晰轮廓", accent: "#d9a98f" },
    { key: "face-w-bright", label: "明朗方圆", accent: "#f0cdb9" }
  ],
  hair: [
    { key: "hair-w-wave", label: "柔光长卷", accent: "#241915" },
    { key: "hair-w-bob", label: "利落短波波", accent: "#2d201b" },
    { key: "hair-w-tail", label: "低马尾", accent: "#181318" },
    { key: "hair-w-shoulder", label: "锁骨直发", accent: "#3c2922" },
    { key: "hair-w-bun", label: "通勤丸子头", accent: "#21191a" },
    { key: "hair-w-layer", label: "轻层次中发", accent: "#4a3028" },
    { key: "hair-w-pixie", label: "轻盈短发", accent: "#1b1c21" },
    { key: "hair-w-curl", label: "都市大卷", accent: "#35221c" }
  ],
  outfit: [
    { key: "outfit-w-jade", label: "玉石绿套装", accent: "#0e675d" },
    { key: "outfit-w-cream", label: "米白立领衫", accent: "#e8e0d4" },
    { key: "outfit-w-navy", label: "深蓝通勤装", accent: "#18314a" },
    { key: "outfit-w-rose", label: "暖玫针织衫", accent: "#9c5364" },
    { key: "outfit-w-sport", label: "雾蓝运动装", accent: "#547487" },
    { key: "outfit-w-copper", label: "焦糖风衣", accent: "#845a3f" }
  ],
  accessory: [
    { key: "accessory-w-pearl", label: "简约珍珠耳饰", accent: "#f0e7db" },
    { key: "accessory-w-glasses", label: "细框眼镜", accent: "#c9b276" },
    { key: "accessory-w-watch", label: "都市腕表", accent: "#93a9b7" },
    { key: "accessory-w-none", label: "无配饰", accent: "#73889a" }
  ],
  idle: [
    { key: "idle-w-poised", label: "从容站姿", accent: "#1fa48e" },
    { key: "idle-w-wave", label: "轻挥手", accent: "#2e7ff2" },
    { key: "idle-w-think", label: "观察思考", accent: "#7658f4" },
    { key: "idle-w-relaxed", label: "轻松呼吸", accent: "#d5b067" }
  ]
} as const satisfies Record<AvatarSlot, readonly CatalogItem[]>;

const man = {
  face: [
    { key: "face-m-soft", label: "温和方圆", accent: "#d8aa92" },
    { key: "face-m-clear", label: "清朗窄面", accent: "#e1b69f" },
    { key: "face-m-defined", label: "利落轮廓", accent: "#c89477" },
    { key: "face-m-bright", label: "明朗宽面", accent: "#e7bba1" }
  ],
  hair: [
    { key: "hair-m-texture", label: "都市纹理短发", accent: "#17191d" },
    { key: "hair-m-side", label: "清爽侧分", accent: "#27201d" },
    { key: "hair-m-crop", label: "利落短寸", accent: "#191b1c" },
    { key: "hair-m-wave", label: "自然微卷", accent: "#34261f" },
    { key: "hair-m-swept", label: "轻背头", accent: "#221b18" },
    { key: "hair-m-layer", label: "层次中短发", accent: "#3e2c24" },
    { key: "hair-m-sport", label: "运动短发", accent: "#13171b" },
    { key: "hair-m-casual", label: "休闲碎发", accent: "#2d2320" }
  ],
  outfit: [
    { key: "outfit-m-navy", label: "深蓝都市夹克", accent: "#142d48" },
    { key: "outfit-m-jade", label: "玉石绿立领衫", accent: "#0e675d" },
    { key: "outfit-m-cream", label: "米白休闲衫", accent: "#ded6c8" },
    { key: "outfit-m-grey", label: "石墨通勤装", accent: "#44505c" },
    { key: "outfit-m-sport", label: "雾蓝运动装", accent: "#486e83" },
    { key: "outfit-m-copper", label: "焦糖休闲外套", accent: "#7e563e" }
  ],
  accessory: [
    { key: "accessory-m-watch", label: "都市腕表", accent: "#9aafbb" },
    { key: "accessory-m-glasses", label: "细框眼镜", accent: "#b69d69" },
    { key: "accessory-m-headphone", label: "颈挂耳机", accent: "#2e3d4e" },
    { key: "accessory-m-none", label: "无配饰", accent: "#73889a" }
  ],
  idle: [
    { key: "idle-m-poised", label: "从容站姿", accent: "#1fa48e" },
    { key: "idle-m-wave", label: "轻挥手", accent: "#2e7ff2" },
    { key: "idle-m-think", label: "观察思考", accent: "#7658f4" },
    { key: "idle-m-relaxed", label: "轻松呼吸", accent: "#d5b067" }
  ]
} as const satisfies Record<AvatarSlot, readonly CatalogItem[]>;

export const AVATAR_CATALOG = { woman, man } as const;

export function catalogItem(gender: AvatarGender, slot: AvatarSlot, key: string): CatalogItem {
  const item = AVATAR_CATALOG[gender][slot].find((candidate) => candidate.key === key);
  if (!item) throw new Error(`Unknown avatar item: ${gender}/${slot}/${key}`);
  return item;
}
